import { C } from "../../lib/colors";
import { GUIDE_CONTENT } from "./guideContent";
import { useT } from "../LangContext";

// ─── Fase 17B · Bloque 2 — Shared guide content renderer ────────────────
// Single source of truth for the guide layout. Used by both the public
// `/guide` page (in `Guide.jsx`) and the in-app `GuideDrawer.jsx`.
// Renders: title block + TOC + sections + FAQ + plan table.
//
// `containerSelector` is used by the smooth-scroll helper to find the
// scroll container — the page uses `window`, the drawer uses its own
// scrollable element. Pass `null` to default to `window`.

export default function GuideBody({ containerSelector = null, showTitle = true }) {
  const { lang } = useT();
  const content = GUIDE_CONTENT[lang] || GUIDE_CONTENT.es;

  return (
    <div data-testid="guide-body">
      {showTitle && (
        <>
          <div style={{ color: C.brass, fontFamily: "var(--mono)", fontSize: 11,
                          letterSpacing: 0.25, marginBottom: 12 }}>
            {content.kicker}
          </div>
          <h1 style={{ fontFamily: "var(--serif), 'Fraunces', serif",
                          fontSize: "clamp(24px, 4.4vw, 38px)",
                          lineHeight: 1.15, color: C.bone, margin: 0, fontWeight: 400 }}>
            {content.title}
          </h1>
          <p style={{ fontFamily: "var(--plex)", fontSize: 14, lineHeight: 1.65, marginTop: 16,
                        color: C.linen }}>
            {content.subtitle}
          </p>
          <p style={{ fontFamily: "var(--plex)", fontSize: 13, lineHeight: 1.6, marginTop: 12,
                        color: C.smoke, fontStyle: "italic" }}>
            {content.intro}
          </p>
        </>
      )}

      {/* TOC */}
      <nav data-testid="guide-toc" style={{ marginTop: 28, padding: "16px 18px",
                                                border: `1px solid ${C.dust}`,
                                                background: "rgba(42, 48, 50, 0.30)" }}>
        <div style={{ color: C.steel, fontFamily: "var(--mono)", fontSize: 10,
                        letterSpacing: 0.25, textTransform: "uppercase", marginBottom: 10 }}>
          {lang === "es" ? "Índice" : "Table of contents"}
        </div>
        <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
          {content.toc.map(item => (
            <li key={item.id}>
              <a href={`#${item.id}`} data-testid={`guide-toc-${item.id}`}
                 onClick={(e) => smoothScroll(e, item.id, containerSelector)}
                 style={{ color: C.brass, fontFamily: "var(--plex)", fontSize: 13,
                            textDecoration: "none", letterSpacing: 0.05, lineHeight: 1.55 }}>
                → {item.label}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {/* Sections */}
      {content.sections.map(section => (
        <SectionRender key={section.id} section={section} />
      ))}

      {/* FAQ */}
      <section id="faq" style={{ marginTop: 48 }} data-testid="guide-faq">
        <div style={{ color: C.brass, fontFamily: "var(--mono)", fontSize: 10,
                        letterSpacing: 0.25, marginBottom: 6 }}>07</div>
        <h2 style={{ fontFamily: "var(--serif), 'Fraunces', serif", fontSize: 26,
                        fontWeight: 400, color: C.bone, margin: "0 0 18px" }}>
          {content.faqTitle}
        </h2>
        {content.faq.map((item, i) => (
          <details key={i} style={{ marginBottom: 12, borderBottom: `1px dashed ${C.dust}`,
                                       paddingBottom: 10 }}>
            <summary style={{ cursor: "pointer", fontFamily: "var(--plex)", fontSize: 14,
                                color: C.linen, padding: "8px 0", letterSpacing: 0.05 }}>
              {item.q}
            </summary>
            <p style={{ fontFamily: "var(--plex)", fontSize: 13, lineHeight: 1.6,
                          color: C.smoke, margin: "6px 0 0" }}>
              {item.a}
            </p>
          </details>
        ))}
      </section>

      {/* Plan table */}
      <section id="planes" style={{ marginTop: 48 }} data-testid="guide-plan-table">
        <div style={{ color: C.brass, fontFamily: "var(--mono)", fontSize: 10,
                        letterSpacing: 0.25, marginBottom: 6 }}>08</div>
        <h2 style={{ fontFamily: "var(--serif), 'Fraunces', serif", fontSize: 26,
                        fontWeight: 400, color: C.bone, margin: "0 0 18px" }}>
          {content.planTable.title}
        </h2>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse",
                            fontFamily: "var(--plex)", fontSize: 12.5, minWidth: 480 }}>
            <thead>
              <tr>
                {content.planTable.headers.map((h, i) => (
                  <th key={i} style={{ padding: "10px 8px", borderBottom: `1px solid ${C.brass}`,
                                          color: i === 0 ? C.smoke : C.brass, textAlign: i === 0 ? "left" : "center",
                                          fontWeight: 600, letterSpacing: 0.1, fontSize: 11 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {content.planTable.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j} style={{ padding: "10px 8px",
                                            borderBottom: `1px dashed ${C.dust}`,
                                            color: j === 0 ? C.linen : C.bone,
                                            textAlign: j === 0 ? "left" : "center",
                                            fontFamily: j === 0 ? "var(--plex)" : "var(--mono)" }}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function smoothScroll(e, id, containerSelector) {
  e.preventDefault();
  const target = document.getElementById(id);
  if (!target) return;
  if (containerSelector) {
    const c = document.querySelector(containerSelector);
    if (c) {
      const offset = target.getBoundingClientRect().top - c.getBoundingClientRect().top + c.scrollTop;
      c.scrollTo({ top: offset - 8, behavior: "smooth" });
      return;
    }
  }
  target.scrollIntoView({ behavior: "smooth", block: "start" });
}

function SectionRender({ section }) {
  return (
    <section id={section.id} data-testid={`guide-section-${section.id}`}
             style={{ marginTop: 40, scrollMarginTop: 80 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
        <span style={{ color: C.brass, fontFamily: "var(--mono)", fontSize: 11,
                        letterSpacing: 0.25 }}>{section.kicker}</span>
        <h2 style={{ fontFamily: "var(--serif), 'Fraunces', serif", fontSize: "clamp(20px, 3.6vw, 26px)",
                        fontWeight: 400, color: C.bone, margin: 0, flex: 1, lineHeight: 1.25 }}>
          {section.title}
        </h2>
        {section.time && (
          <span style={{ color: C.smoke, fontFamily: "var(--mono)", fontSize: 11,
                          fontStyle: "italic" }}>
            ({section.time})
          </span>
        )}
      </div>
      {section.blocks.map((block, i) => <BlockRender key={i} block={block} />)}
    </section>
  );
}

function BlockRender({ block }) {
  if (block.type === "p") {
    return (
      <p style={{ fontFamily: "var(--plex)", fontSize: 14, lineHeight: 1.7,
                    color: C.linen, margin: "12px 0" }}>
        {renderBold(block.text)}
      </p>
    );
  }
  if (block.type === "h3") {
    return (
      <h3 style={{ fontFamily: "var(--plex)", fontSize: 15, fontWeight: 600,
                     color: C.brass, letterSpacing: 0.1, margin: "20px 0 8px",
                     textTransform: "none" }}>
        {block.text}
      </h3>
    );
  }
  if (block.type === "list") {
    return (
      <ul style={{ fontFamily: "var(--plex)", fontSize: 14, lineHeight: 1.7,
                     color: C.linen, paddingLeft: 18, margin: "8px 0" }}>
        {block.items.map((item, i) => (
          <li key={i} style={{ marginBottom: 6 }}>{renderBold(item)}</li>
        ))}
      </ul>
    );
  }
  if (block.type === "steps") {
    return (
      <ol style={{ fontFamily: "var(--plex)", fontSize: 14, lineHeight: 1.7,
                     color: C.linen, paddingLeft: 22, margin: "8px 0",
                     counterReset: "step" }}>
        {block.items.map((item, i) => (
          <li key={i} style={{ marginBottom: 6 }}>{renderBold(item)}</li>
        ))}
      </ol>
    );
  }
  if (block.type === "tip") {
    return (
      <div style={{ margin: "14px 0", padding: "10px 14px",
                     borderLeft: `2px solid ${C.brass}`,
                     background: `${C.brass}0A`,
                     fontFamily: "var(--plex)", fontSize: 13, lineHeight: 1.6,
                     color: C.linen }}>
        ★ {renderBold(block.text)}
      </div>
    );
  }
  return null;
}

function renderBold(text) {
  if (!text || typeof text !== "string") return text;
  const parts = [];
  let lastIdx = 0;
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    if (m[2] !== undefined) {
      parts.push(<strong key={key++} style={{ color: C.bone }}>{m[2]}</strong>);
    } else if (m[3] !== undefined) {
      parts.push(<em key={key++}>{m[3]}</em>);
    }
    lastIdx = re.lastIndex;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts.length ? parts : text;
}
